import dayjs from 'dayjs';
import type { AlertActivityOwner, AlertActivityRow } from '@/apis/shipment';

/** "4 alerts", "1 alert", or "-" when the shipment has no alerts. */
export const formatAlertCount = (total: number | null | undefined): string => {
  const count = Number(total ?? 0);
  if (!Number.isFinite(count) || count <= 0) return '-';
  return count === 1 ? '1 alert' : `${count} alerts`;
};

/**
 * Text of the state pill shown on the right of a popover row:
 * open -> "To review", dismissed -> the dismissal tag text when there is one,
 * otherwise "Dismissed", resolved by AI -> "Resolved via AI", otherwise the
 * resolution tag text when there is one.
 * The shared Pill component picks its own colours from this text.
 */
export const getAlertPillLabel = (
  row: Pick<AlertActivityRow, 'state' | 'lifecycle' | 'close_tag'>
): string => {
  if (row.state === 'OPEN') return 'To review';
  if (row.state === 'DISMISSED') return row.close_tag?.label || 'Dismissed';
  if (row.lifecycle === 'resolved_via_ai') return 'Resolved via AI';
  return row.close_tag?.label || 'Resolved';
};

/** "@John Doe" for one owner, "@John Doe +2" for several, "" when unassigned. */
export const formatAlertOwners = (
  owners: AlertActivityOwner[] | null | undefined
): string => {
  const names = (owners ?? []).map((owner) => owner.name).filter(Boolean);
  if (names.length === 0) return '';
  return names.length === 1
    ? `@${names[0]}`
    : `@${names[0]} +${names.length - 1}`;
};

/** Same wording as the rest of the shipment list: "Now", "5 m ago", "3 hrs ago". */
export const formatAlertRelativeTime = (
  iso: string | null | undefined,
  now: Date = new Date()
): string => {
  if (!iso) return '';
  const created = dayjs(iso);
  if (!created.isValid()) return '';
  const diff = Math.max(0, dayjs(now).diff(created));
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'Now';
  if (minutes < 60) return `${minutes} m ago`;
  if (hours < 24) return `${hours} hrs ago`;
  return `${days} days ago`;
};
